import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  FileText, Search, Printer, TrendingUp, TrendingDown,
  Wallet, BadgeDollarSign, ChevronDown, ChevronUp, CheckCircle2,
  Clock, AlertCircle, XCircle, CreditCard, User, Calendar,
  Loader2, X, Filter, Receipt,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { formatLocalDate } from '@/lib/utils';

// ─── Types ────────────────────────────────────────────────────────────────────
interface AlumnoResult {
  id: number;
  nombre: string;
  apellidoPaterno: string;
  nui: string;
}

interface InvoicePayment {
  id: number;
  date: string;
  amount: number;
  paymentMethod: string;
  reference: string | null;
  notes: string | null;
}

interface InvoiceItem {
  description: string | null;
  quantity: string;
  unitPrice: string;
  total: string;
}

interface InvoiceRow {
  id: number;
  invoiceNumber: string;
  date: string;
  dueDate: string;
  status: 'PAID' | 'ISSUED' | 'PARTIALLY_PAID' | 'CANCELLED';
  total: number;
  subtotal: number;
  taxTotal: number;
  pagado: number;
  pendiente: number;
  notes: string | null;
  payments: InvoicePayment[];
  items: InvoiceItem[];
  hasCfdi: boolean;
  cfdiMetodoPago: string | null;
  cfdiSaldoInsoluto: string | null;
}

interface InvoicingProfileLite {
  id: number;
  rfc: string;
  legalName: string;
  hasHubConfig: boolean;
  isDefault: boolean;
}

interface TaxProfileLite {
  id: number;
  rfc: string;
  profileName: string;
}

interface EstadoCuentaData {
  alumno: {
    id: number;
    nui: string;
    nombre: string;
    apellidoPaterno: string;
    apellidoMaterno: string | null;
    email: string | null;
    telefono: string | null;
    estatus: string;
    groupName: string | null;
  };
  invoices: InvoiceRow[];
  creditBalance: number;
  summary: {
    totalFacturado: number;
    totalPagado: number;
    totalPendiente: number;
    creditBalance: number;
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (n: number) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n);

const fmtDate = (d: string | null | undefined) => {
  if (!d) return '—';
  // Fechas date-only (sin hora significativa) — se leen los dígitos de
  // calendario tal cual en vez de convertir por timezone, que es lo único
  // inmune a cómo el backend haya anclado el instante almacenado.
  return formatLocalDate(d, { day: '2-digit', month: 'short', year: 'numeric' });
};

const STATUS_LABEL: Record<string, string> = {
  PAID: 'Pagado',
  ISSUED: 'Pendiente',
  PARTIALLY_PAID: 'Parcial',
  CANCELLED: 'Cancelado',
};

const STATUS_CLASS: Record<string, string> = {
  PAID: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  ISSUED: 'bg-amber-100 text-amber-700 border-amber-200',
  PARTIALLY_PAID: 'bg-blue-100 text-blue-700 border-blue-200',
  CANCELLED: 'bg-red-100 text-red-700 border-red-200',
};

const STATUS_ICON: Record<string, JSX.Element> = {
  PAID: <CheckCircle2 className="w-3 h-3" />,
  ISSUED: <Clock className="w-3 h-3" />,
  PARTIALLY_PAID: <AlertCircle className="w-3 h-3" />,
  CANCELLED: <XCircle className="w-3 h-3" />,
};

const METODO: Record<string, string> = {
  TRANSFER: 'Transferencia',
  CASH: 'Efectivo',
  CARD: 'Tarjeta',
  CHECK: 'Cheque',
  OTHER: 'Otro',
  CREDIT: 'Saldo a Favor',
};

// ─── Main Component ───────────────────────────────────────────────────────────
export default function EstadoCuenta() {
  const [alumnoSearch, setAlumnoSearch] = useState('');
  const [alumnoResults, setAlumnoResults] = useState<AlumnoResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedAlumno, setSelectedAlumno] = useState<AlumnoResult | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const dropdownRef = useRef<HTMLDivElement>(null);
  const printRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const savedCursor = useRef<{ start: number; end: number } | null>(null);

  // ── Filters ─────────────────────────────────────────────────────────────────
  const [folioFilter, setFolioFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [conceptoFilter, setConceptoFilter] = useState('');

  // ── "Facturar PPD" dialog state ──────────────────────────────────────────────
  const [ppdDialogInvoice, setPpdDialogInvoice] = useState<InvoiceRow | null>(null);
  const [ppdSettingsId, setPpdSettingsId] = useState<number | null>(null);
  const [ppdTaxProfileId, setPpdTaxProfileId] = useState('');
  const [ppdSubmitting, setPpdSubmitting] = useState(false);
  const [ppdError, setPpdError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Debounced alumno search
  const searchAlumnos = useCallback(async (q: string) => {
    if (!q || q.length < 2) { setAlumnoResults([]); return; }
    // Guardar posición del cursor antes de re-renders por setSearching
    const el = searchInputRef.current;
    savedCursor.current = el
      ? { start: el.selectionStart ?? 0, end: el.selectionEnd ?? 0 }
      : null;
    setSearching(true);
    try {
      const res = await fetch(`/api/alumnos?search=${encodeURIComponent(q)}&limit=15`, { credentials: 'include' });
      const data = await res.json();
      const list: AlumnoResult[] = (data.alumnos ?? data ?? []).map((a: any) => ({
        id: a.id,
        nombre: a.nombre,
        apellidoPaterno: a.apellidoPaterno || '',
        nui: a.numeroIdentificacionUnico || '',
      }));
      setAlumnoResults(list);
      setShowDropdown(true);
    } catch { setAlumnoResults([]); }
    finally {
      setSearching(false);
      // Restaurar foco y posición del cursor tras los re-renders
      requestAnimationFrame(() => {
        const cur = savedCursor.current;
        if (cur && searchInputRef.current) {
          searchInputRef.current.focus();
          searchInputRef.current.setSelectionRange(cur.start, cur.end);
        }
        savedCursor.current = null;
      });
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => searchAlumnos(alumnoSearch), 300);
    return () => clearTimeout(t);
  }, [alumnoSearch, searchAlumnos]);

  // Fetch estado de cuenta
  const { data, isLoading, isError } = useQuery<EstadoCuentaData>({
    queryKey: ['estado-cuenta', selectedAlumno?.id],
    queryFn: async () => {
      const res = await fetch(`/api/sales/estado-cuenta?alumnoId=${selectedAlumno!.id}`, { credentials: 'include' });
      if (!res.ok) { const e = await res.json(); throw new Error(e.error || 'Error'); }
      return res.json();
    },
    enabled: !!selectedAlumno,
  });

  // ── "Facturar PPD" dialog data ────────────────────────────────────────────────
  const { data: invoicingProfiles = [] } = useQuery<InvoicingProfileLite[]>({
    queryKey: ['cfdi-settings-all-estado-cuenta'],
    queryFn: () => fetch('/api/cfdi/settings/all', { credentials: 'include' }).then(r => r.json()),
    enabled: !!ppdDialogInvoice,
  });

  const { data: ppdTaxProfiles = [] } = useQuery<TaxProfileLite[]>({
    queryKey: ['cfdi-tax-profiles-estado-cuenta', selectedAlumno?.id],
    queryFn: () => fetch(`/api/cfdi/tax-profiles?alumnoId=${selectedAlumno!.id}`, { credentials: 'include' }).then(r => r.json()),
    enabled: !!ppdDialogInvoice && !!selectedAlumno,
  });

  useEffect(() => {
    if (ppdDialogInvoice && ppdSettingsId === null && invoicingProfiles.length > 0) {
      const first = invoicingProfiles.find(p => p.hasHubConfig);
      if (first) setPpdSettingsId(first.id);
    }
  }, [ppdDialogInvoice, invoicingProfiles, ppdSettingsId]);

  const openPpdDialog = (inv: InvoiceRow) => {
    setPpdDialogInvoice(inv);
    setPpdSettingsId(null);
    setPpdTaxProfileId('');
    setPpdError(null);
  };

  const closePpdDialog = () => {
    setPpdDialogInvoice(null);
    setPpdSettingsId(null);
    setPpdTaxProfileId('');
    setPpdError(null);
  };

  const handleIssuePpd = async () => {
    if (!ppdDialogInvoice || !ppdSettingsId) return;
    if (!ppdTaxProfileId) { setPpdError('Selecciona un receptor'); return; }
    setPpdSubmitting(true);
    setPpdError(null);
    try {
      const body: Record<string, unknown> = { invoiceId: ppdDialogInvoice.id, settingsId: ppdSettingsId };
      if (ppdTaxProfileId === '-1') body.publicoGeneral = true;
      else body.taxProfileId = Number(ppdTaxProfileId);

      const r = await fetch('/api/cfdi/ppd/issue', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      const json = await r.json();
      if (!r.ok) throw new Error(json.error || 'Error al timbrar factura PPD');
      closePpdDialog();
      queryClient.invalidateQueries({ queryKey: ['estado-cuenta', selectedAlumno?.id] });
    } catch (err: any) {
      setPpdError(err.message || 'Error al timbrar factura PPD');
    } finally {
      setPpdSubmitting(false);
    }
  };

  // Clear filters when student changes
  useEffect(() => {
    setFolioFilter('');
    setStatusFilter('');
    setDateFrom('');
    setDateTo('');
    setConceptoFilter('');
    setExpanded(new Set());
  }, [selectedAlumno?.id]);

  const toggleExpand = (id: number) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // ── Filtered invoices ────────────────────────────────────────────────────────
  const filteredInvoices = useMemo(() => {
    if (!data) return [];
    return data.invoices.filter(inv => {
      if (folioFilter && !inv.invoiceNumber.toLowerCase().includes(folioFilter.toLowerCase())) return false;
      if (statusFilter && inv.status !== statusFilter) return false;
      if (dateFrom && inv.date && new Date(inv.date) < new Date(dateFrom + 'T00:00:00')) return false;
      if (dateTo && inv.date && new Date(inv.date) > new Date(dateTo + 'T23:59:59')) return false;
      if (conceptoFilter) {
        const q = conceptoFilter.toLowerCase();
        const match = inv.items.some(it => (it.description ?? '').toLowerCase().includes(q));
        if (!match) return false;
      }
      return true;
    });
  }, [data, folioFilter, statusFilter, dateFrom, dateTo, conceptoFilter]);

  const filteredSummary = useMemo(() => ({
    totalFacturado: filteredInvoices.filter(i => i.status !== 'CANCELLED').reduce((s, i) => s + i.total, 0),
    totalPagado: filteredInvoices.reduce((s, i) => s + i.pagado, 0),
    totalPendiente: filteredInvoices.reduce((s, i) => s + i.pendiente, 0),
  }), [filteredInvoices]);

  const hasActiveFilters = !!(folioFilter || statusFilter || dateFrom || dateTo || conceptoFilter);

  const clearFilters = () => {
    setFolioFilter('');
    setStatusFilter('');
    setDateFrom('');
    setDateTo('');
    setConceptoFilter('');
  };

  const today = new Date().toLocaleDateString('es-MX', {
    day: '2-digit', month: 'long', year: 'numeric',
  });

  const handlePrint = () => window.print();

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50 print:bg-white">
      {/* Print-specific styles */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-break { page-break-after: always; }
          body { font-size: 11px; }
        }
      `}</style>

      {/* Top bar */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 no-print">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center">
              <FileText className="w-4 h-4 text-white" />
            </div>
            <div>
              <h1 className="text-base font-semibold text-gray-900">Estado de Cuenta</h1>
              <p className="text-xs text-gray-500">Ventas · Reportes financieros por alumno</p>
            </div>
          </div>
          {data && (
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="gap-1.5 text-gray-600" onClick={handlePrint}>
                <Printer className="w-3.5 h-3.5" />
                Imprimir
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6 space-y-5" ref={printRef}>

        {/* ── Student selector ── */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 no-print">
          <p className="text-xs font-medium text-gray-600 mb-2">Buscar alumno</p>
          <div className="flex items-center gap-3 max-w-2xl" ref={dropdownRef}>
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={selectedAlumno ? `${selectedAlumno.nui} — ${selectedAlumno.nombre} ${selectedAlumno.apellidoPaterno}` : alumnoSearch}
                onChange={e => {
                  if (selectedAlumno) {
                    setSelectedAlumno(null);
                    setAlumnoSearch(e.target.value);
                  } else {
                    setAlumnoSearch(e.target.value);
                  }
                  setShowDropdown(true);
                }}
                onFocus={() => { if (alumnoResults.length > 0) setShowDropdown(true); }}
                placeholder="Escribe NUI, nombre o apellido…"
                className="w-full pl-9 pr-9 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
              {searching && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 animate-spin" />
              )}
              {selectedAlumno && !searching && (
                <button
                  onClick={() => { setSelectedAlumno(null); setAlumnoSearch(''); setAlumnoResults([]); }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
              {/* Dropdown */}
              {showDropdown && alumnoResults.length > 0 && !selectedAlumno && (
                <div className="absolute top-full mt-1 left-0 right-0 z-50 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                  {alumnoResults.map(a => (
                    <button
                      key={a.id}
                      onClick={() => {
                        setSelectedAlumno(a);
                        setAlumnoSearch('');
                        setAlumnoResults([]);
                        setShowDropdown(false);
                        setExpanded(new Set());
                      }}
                      className="w-full text-left px-4 py-2.5 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-0"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono text-indigo-600 font-medium">{a.nui}</span>
                        <span className="text-xs text-gray-400">·</span>
                        <span className="text-sm text-gray-800">{a.nombre} {a.apellidoPaterno}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {showDropdown && alumnoSearch.length >= 2 && alumnoResults.length === 0 && !searching && (
                <div className="absolute top-full mt-1 left-0 right-0 z-50 bg-white border border-gray-200 rounded-lg shadow-lg">
                  <p className="px-4 py-3 text-sm text-gray-400">Sin resultados para "{alumnoSearch}"</p>
                </div>
              )}
            </div>
            {selectedAlumno && (
              <Badge variant="outline" className="text-xs border-indigo-200 text-indigo-600 bg-indigo-50 flex-shrink-0">
                {selectedAlumno.nui}
              </Badge>
            )}
          </div>
        </div>

        {/* ── Empty state ── */}
        {!selectedAlumno && (
          <div className="bg-white rounded-xl border border-dashed border-gray-300 py-20 text-center">
            <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
              <User className="w-6 h-6 text-gray-400" />
            </div>
            <p className="text-sm font-medium text-gray-500">Selecciona un alumno para ver su estado de cuenta</p>
            <p className="text-xs text-gray-400 mt-1">Busca por NUI, nombre o apellido</p>
          </div>
        )}

        {/* ── Loading ── */}
        {selectedAlumno && isLoading && (
          <div className="bg-white rounded-xl border border-gray-200 py-20 flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 text-indigo-500 animate-spin" />
            <p className="text-sm text-gray-500">Cargando estado de cuenta…</p>
          </div>
        )}

        {/* ── Error ── */}
        {selectedAlumno && isError && (
          <div className="bg-red-50 rounded-xl border border-red-200 py-10 text-center">
            <p className="text-sm text-red-600">No se pudo cargar el estado de cuenta.</p>
          </div>
        )}

        {/* ── Main report content ── */}
        {data && !isLoading && (
          <>
            {/* Print header (hidden on screen) */}
            <div className="hidden print:block mb-6">
              <h1 className="text-xl font-bold text-gray-900">Estado de Cuenta</h1>
              <p className="text-xs text-gray-500 mt-0.5">Generado el {today}</p>
            </div>

            {/* Student info card */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 rounded-full bg-indigo-100 flex items-center justify-center no-print">
                    <User className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <h2 className="text-base font-semibold text-gray-900">
                      {data.alumno.nombre} {data.alumno.apellidoPaterno} {data.alumno.apellidoMaterno ?? ''}
                    </h2>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
                      <span className="text-xs text-gray-500 flex items-center gap-1">
                        <BadgeDollarSign className="w-3.5 h-3.5 no-print" /> {data.alumno.nui}
                      </span>
                      {data.alumno.groupName && (
                        <>
                          <span className="text-xs text-gray-400">·</span>
                          <span className="text-xs text-gray-500 flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5 no-print" /> {data.alumno.groupName}
                          </span>
                        </>
                      )}
                      {data.alumno.email && (
                        <>
                          <span className="text-xs text-gray-400">·</span>
                          <span className="text-xs text-gray-400">{data.alumno.email}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="text-right text-xs text-gray-400 no-print">
                  <p>Generado: {today}</p>
                </div>
              </div>
            </div>

            {/* Summary metrics */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {/* Total Facturado */}
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs text-gray-500 font-medium">Total Facturado</p>
                    <p className="text-xl font-bold text-gray-900 mt-1">{fmt(data.summary.totalFacturado)}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{data.invoices.filter(i => i.status !== 'CANCELLED').length} facturas</p>
                  </div>
                  <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <FileText className="w-4 h-4 text-gray-500" />
                  </div>
                </div>
              </div>

              {/* Total Pagado */}
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs text-gray-500 font-medium">Total Pagado</p>
                    <p className="text-xl font-bold text-emerald-600 mt-1">{fmt(data.summary.totalPagado)}</p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {data.summary.totalFacturado > 0
                        ? `${Math.round((data.summary.totalPagado / data.summary.totalFacturado) * 100)}% del total`
                        : '—'}
                    </p>
                  </div>
                  <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-4 h-4 text-emerald-500" />
                  </div>
                </div>
              </div>

              {/* Saldo Pendiente */}
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs text-gray-500 font-medium">Saldo Pendiente</p>
                    <p className={`text-xl font-bold mt-1 ${data.summary.totalPendiente > 0 ? 'text-amber-600' : 'text-gray-400'}`}>
                      {fmt(data.summary.totalPendiente)}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {data.invoices.filter(i => i.status === 'ISSUED' || i.status === 'PARTIALLY_PAID').length} cuenta(s) abiertas
                    </p>
                  </div>
                  <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
                    <TrendingDown className="w-4 h-4 text-amber-500" />
                  </div>
                </div>
              </div>

              {/* Saldo a Favor */}
              <div className={`rounded-xl border p-4 ${data.creditBalance > 0 ? 'bg-violet-50 border-violet-200' : 'bg-white border-gray-200'}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className={`text-xs font-medium ${data.creditBalance > 0 ? 'text-violet-600' : 'text-gray-500'}`}>
                      Saldo a Favor
                    </p>
                    <p className={`text-xl font-bold mt-1 ${data.creditBalance > 0 ? 'text-violet-700' : 'text-gray-400'}`}>
                      {fmt(data.creditBalance)}
                    </p>
                    <p className={`text-xs mt-0.5 ${data.creditBalance > 0 ? 'text-violet-500' : 'text-gray-400'}`}>
                      {data.creditBalance > 0 ? 'Disponible para aplicar' : 'Sin saldo a favor'}
                    </p>
                  </div>
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${data.creditBalance > 0 ? 'bg-violet-100' : 'bg-gray-100'}`}>
                    <Wallet className={`w-4 h-4 ${data.creditBalance > 0 ? 'text-violet-600' : 'text-gray-400'}`} />
                  </div>
                </div>
              </div>
            </div>

            {/* Invoices table */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold text-gray-800">Cuentas de Cobro y Pagos</h3>
                  {hasActiveFilters && (
                    <span className="inline-flex items-center gap-1 text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full border border-indigo-200 font-medium">
                      <Filter className="w-3 h-3" />
                      Filtros activos
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  {hasActiveFilters && (
                    <button
                      onClick={clearFilters}
                      className="text-xs text-gray-400 hover:text-red-500 flex items-center gap-1 transition-colors"
                    >
                      <X className="w-3 h-3" /> Limpiar filtros
                    </button>
                  )}
                  <span className="text-xs text-gray-400">
                    {hasActiveFilters
                      ? `${filteredInvoices.length} de ${data.invoices.length} registros`
                      : `${data.invoices.length} registros`}
                  </span>
                </div>
              </div>

              {data.invoices.length === 0 ? (
                <div className="py-16 text-center">
                  <FileText className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-400">Este alumno no tiene facturas registradas</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {/* Table header with inline filters */}
                  <div className="bg-gray-50 border-b border-gray-200">
                    {/* Column labels */}
                    <div className="grid grid-cols-12 gap-2 px-5 pt-2.5 pb-1 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      <div className="col-span-1" />
                      <div className="col-span-2">Folio</div>
                      <div className="col-span-2">Fecha emisión</div>
                      <div className="col-span-3">Descripción</div>
                      <div className="col-span-1 text-right">Total</div>
                      <div className="col-span-1 text-right">Pagado</div>
                      <div className="col-span-1 text-right">Pendiente</div>
                      <div className="col-span-1 text-right">Estatus</div>
                    </div>
                    {/* Filter controls */}
                    <div className="grid grid-cols-12 gap-2 px-5 pb-2.5 items-center">
                      <div className="col-span-1" />
                      {/* Folio filter */}
                      <div className="col-span-2">
                        <input
                          type="text"
                          value={folioFilter}
                          onChange={e => setFolioFilter(e.target.value)}
                          placeholder="Buscar folio…"
                          className="w-full text-xs px-2 py-1 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-400 focus:border-indigo-400 bg-white placeholder-gray-300"
                        />
                      </div>
                      {/* Date range filter */}
                      <div className="col-span-2 flex gap-1">
                        <input
                          type="date"
                          value={dateFrom}
                          onChange={e => setDateFrom(e.target.value)}
                          title="Fecha desde"
                          className="w-full text-xs px-1 py-1 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-400 focus:border-indigo-400 bg-white"
                        />
                        <input
                          type="date"
                          value={dateTo}
                          onChange={e => setDateTo(e.target.value)}
                          title="Fecha hasta"
                          className="w-full text-xs px-1 py-1 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-400 focus:border-indigo-400 bg-white"
                        />
                      </div>
                      {/* Concepto filter */}
                      <div className="col-span-3">
                        <input
                          type="text"
                          value={conceptoFilter}
                          onChange={e => setConceptoFilter(e.target.value)}
                          placeholder="Buscar concepto…"
                          className="w-full text-xs px-2 py-1 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-400 focus:border-indigo-400 bg-white placeholder-gray-300"
                        />
                      </div>
                      {/* Total, Pagado, Pendiente — no filter */}
                      <div className="col-span-1" />
                      <div className="col-span-1" />
                      <div className="col-span-1" />
                      {/* Status filter */}
                      <div className="col-span-1 flex justify-end">
                        <select
                          value={statusFilter}
                          onChange={e => setStatusFilter(e.target.value)}
                          className={`text-xs px-1.5 py-1 border rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-400 focus:border-indigo-400 bg-white w-full ${statusFilter ? 'border-indigo-300 text-indigo-700 bg-indigo-50' : 'border-gray-200 text-gray-500'}`}
                        >
                          <option value="">Todos</option>
                          <option value="ISSUED">Pendiente</option>
                          <option value="PAID">Pagado</option>
                          <option value="PARTIALLY_PAID">Parcial</option>
                          <option value="CANCELLED">Cancelado</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Empty filtered state */}
                  {filteredInvoices.length === 0 && (
                    <div className="py-12 text-center">
                      <Filter className="w-7 h-7 text-gray-300 mx-auto mb-2" />
                      <p className="text-sm text-gray-400">Ninguna factura coincide con los filtros aplicados</p>
                      <button onClick={clearFilters} className="mt-2 text-xs text-indigo-500 hover:underline">
                        Limpiar filtros
                      </button>
                    </div>
                  )}

                  {filteredInvoices.map(inv => {
                    const isExpanded = expanded.has(inv.id);
                    const isOverdue = inv.status !== 'PAID' && inv.status !== 'CANCELLED'
                      && inv.dueDate && new Date(inv.dueDate) < new Date();

                    return (
                      <div key={inv.id}>
                        {/* Invoice row */}
                        <div
                          className={`grid grid-cols-12 gap-2 px-5 py-3.5 items-center text-sm cursor-pointer hover:bg-gray-50 transition-colors ${isOverdue ? 'bg-red-50/50' : ''}`}
                          onClick={() => { if (inv.payments.length > 0) toggleExpand(inv.id); }}
                        >
                          <div className="col-span-1">
                            {inv.payments.length > 0
                              ? isExpanded
                                ? <ChevronUp className="w-4 h-4 text-gray-400" />
                                : <ChevronDown className="w-4 h-4 text-gray-400" />
                              : <div className="w-4" />
                            }
                          </div>
                          <div className="col-span-2 font-mono text-xs text-indigo-600 font-medium leading-tight">
                            {inv.invoiceNumber}
                          </div>
                          <div className="col-span-2 text-xs">
                            <div className="text-gray-600">{fmtDate(inv.date)}</div>
                            <div className={`mt-0.5 ${isOverdue ? 'text-red-500 font-medium' : 'text-gray-400'}`}>
                              {isOverdue ? '⚠ Venc. ' : 'Vence: '}{fmtDate(inv.dueDate)}
                            </div>
                          </div>
                          <div className="col-span-3 text-gray-700 text-sm leading-tight">
                            {inv.items.length > 0
                              ? inv.items.map((it, i) => (
                                  <div key={i} className={i > 0 ? 'text-xs text-gray-400 mt-0.5' : ''}>
                                    {it.description || '—'}
                                  </div>
                                ))
                              : <span className="text-gray-400 italic text-xs">Sin detalle</span>
                            }
                          </div>
                          <div className="col-span-1 text-right font-medium text-gray-800">
                            {fmt(inv.total)}
                          </div>
                          <div className="col-span-1 text-right text-emerald-600 font-medium">
                            {inv.pagado > 0 ? fmt(inv.pagado) : <span className="text-gray-300">—</span>}
                          </div>
                          <div className="col-span-1 text-right font-medium">
                            {inv.pendiente > 0.005
                              ? <span className={isOverdue ? 'text-red-500' : 'text-amber-600'}>{fmt(inv.pendiente)}</span>
                              : <span className="text-gray-300">—</span>
                            }
                          </div>
                          <div className="col-span-1 flex flex-col items-end gap-1">
                            <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border font-medium ${STATUS_CLASS[inv.status] ?? 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                              {STATUS_ICON[inv.status]}
                              {STATUS_LABEL[inv.status] ?? inv.status}
                            </span>
                            {inv.cfdiMetodoPago === 'PPD' && (
                              <span className="no-print inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border border-sky-200 bg-sky-50 text-sky-700 font-medium">
                                <Receipt className="w-3 h-3" />
                                PPD · saldo {fmt(parseFloat(inv.cfdiSaldoInsoluto || '0'))}
                              </span>
                            )}
                            {!inv.hasCfdi && inv.status !== 'CANCELLED' && (
                              <button
                                onClick={e => { e.stopPropagation(); openPpdDialog(inv); }}
                                className="no-print text-xs text-indigo-600 hover:underline"
                              >
                                Facturar PPD
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Expanded payments */}
                        {isExpanded && inv.payments.length > 0 && (
                          <div className="mx-5 mb-3 rounded-lg bg-slate-50 border border-slate-200 overflow-hidden">
                            <div className="grid grid-cols-12 gap-2 px-4 py-2 text-xs font-medium text-slate-500 uppercase tracking-wide bg-slate-100">
                              <div className="col-span-1" />
                              <div className="col-span-3">Fecha de pago</div>
                              <div className="col-span-3">Método</div>
                              <div className="col-span-3">Referencia</div>
                              <div className="col-span-2 text-right">Monto</div>
                            </div>
                            {inv.payments.map(p => (
                              <div key={p.id} className="grid grid-cols-12 gap-2 px-4 py-2.5 items-center border-t border-slate-200 first:border-0">
                                <div className="col-span-1 flex justify-center">
                                  <CreditCard className="w-3.5 h-3.5 text-slate-400" />
                                </div>
                                <div className="col-span-3 text-xs text-slate-600">{fmtDate(String(p.date))}</div>
                                <div className="col-span-3 text-xs text-slate-600">{METODO[p.paymentMethod] ?? p.paymentMethod}</div>
                                <div className="col-span-3 text-xs text-slate-500 font-mono">{p.reference || '—'}</div>
                                <div className="col-span-2 text-right text-emerald-600 font-semibold text-xs">{fmt(p.amount)}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Totals row */}
                  {filteredInvoices.length > 0 && (
                    <div className="grid grid-cols-12 gap-2 px-5 py-3 bg-gray-50 border-t border-gray-200">
                      <div className="col-span-8 text-xs font-bold text-gray-700 uppercase tracking-wide flex items-center gap-1.5">
                        Totales
                        {hasActiveFilters && <span className="font-normal text-gray-400 normal-case">(filtrados)</span>}
                      </div>
                      <div className="col-span-1 text-right text-sm font-bold text-gray-800">{fmt(filteredSummary.totalFacturado)}</div>
                      <div className="col-span-1 text-right text-sm font-bold text-emerald-600">{fmt(filteredSummary.totalPagado)}</div>
                      <div className="col-span-1 text-right text-sm font-bold text-amber-600">
                        {filteredSummary.totalPendiente > 0 ? fmt(filteredSummary.totalPendiente) : '—'}
                      </div>
                      <div className="col-span-1" />
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Credit balance notice */}
            {data.creditBalance > 0 && (
              <div className="rounded-xl border border-violet-200 bg-violet-50 p-4 flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Wallet className="w-4 h-4 text-violet-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-violet-800">
                    Saldo a Favor disponible: {fmt(data.creditBalance)}
                  </p>
                  <p className="text-xs text-violet-600 mt-0.5">
                    Este alumno tiene crédito disponible que puede aplicarse a cuentas pendientes
                    {data.summary.totalPendiente > 0 && ` (${fmt(data.summary.totalPendiente)} pendiente)`}.
                    Ve a Tesorería → Saldos a Favor para aplicarlo.
                  </p>
                </div>
                <a
                  href="/app/treasury/creditos"
                  className="no-print flex-shrink-0 text-xs border border-violet-300 text-violet-700 hover:bg-violet-100 rounded-md px-3 py-1.5 font-medium transition-colors"
                >
                  Ir a Tesorería
                </a>
              </div>
            )}

            <p className="text-center text-xs text-gray-400 pb-2">
              Estado de cuenta generado el {today} · QCampusOne — Sistema de Control Escolar
            </p>
          </>
        )}
      </div>

      {/* ── Facturar PPD dialog ── */}
      {ppdDialogInvoice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 no-print">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-md p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <Receipt className="w-4 h-4 text-indigo-600" />
                Facturar factura PPD
              </h3>
              <button onClick={closePpdDialog} className="text-gray-400 hover:text-gray-600">
                <X className="w-4 h-4" />
              </button>
            </div>

            <p className="text-xs text-gray-500">
              Se timbrará un CFDI de Ingreso por el monto total ({fmt(ppdDialogInvoice.total)}) con método de pago
              PPD (parcialidades). Los cobros posteriores se facturarán como Complemento de Pago desde la Consola
              de Facturación.
            </p>

            <div>
              <label className="text-xs font-medium text-gray-600">Emisor</label>
              <select
                value={ppdSettingsId ?? ''}
                onChange={e => setPpdSettingsId(e.target.value ? parseInt(e.target.value) : null)}
                className="mt-1 w-full text-sm px-2 py-1.5 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-400"
              >
                <option value="">Seleccionar emisor…</option>
                {invoicingProfiles.filter(p => p.hasHubConfig).map(p => (
                  <option key={p.id} value={p.id}>{p.rfc} — {p.legalName}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-xs font-medium text-gray-600">Receptor</label>
              <select
                value={ppdTaxProfileId}
                onChange={e => setPpdTaxProfileId(e.target.value)}
                className="mt-1 w-full text-sm px-2 py-1.5 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-400"
              >
                <option value="">Seleccionar receptor…</option>
                <option value="-1">RFC Público en General (XAXX010101000)</option>
                {ppdTaxProfiles.map(p => (
                  <option key={p.id} value={p.id}>{p.rfc} — {p.profileName}</option>
                ))}
              </select>
            </div>

            {ppdError && (
              <div className="rounded-md bg-red-50 border border-red-200 p-2.5 text-xs text-red-700">
                {ppdError}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" size="sm" onClick={closePpdDialog} disabled={ppdSubmitting}>
                Cancelar
              </Button>
              <Button size="sm" onClick={handleIssuePpd} disabled={ppdSubmitting || !ppdSettingsId || !ppdTaxProfileId}>
                {ppdSubmitting ? (
                  <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Timbrando…</>
                ) : (
                  'Timbrar factura PPD'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
